// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { log } from '../log/log';
import type { ThreadGateway, ThreadIdentifier, ThreadOpenSnapshot, ThreadState } from './types';

/** Args for {@link useThreadViewModel}. */
export interface UseThreadViewModelArgs {
  readonly gateway: ThreadGateway;
  /** Override only for tests; production uses 250ms. */
  readonly markAsReadDebounceMs?: number;
}

/** Imperative surface returned by the hook. */
export interface ThreadViewModel {
  readonly state: ThreadState;
  readonly open: (identifier: ThreadIdentifier) => void;
  readonly close: () => void;
  readonly retry: () => void;
  readonly sendReply: (body: string) => Promise<void>;
  readonly markAsRead: () => void;
}

const DEFAULT_MARK_AS_READ_DEBOUNCE_MS = 250;

interface Intent {
  readonly kind: 'open' | 'retry';
  readonly identifier: ThreadIdentifier;
  /** Monotonic counter — defends against stale gateway resolutions. */
  readonly nonce: number;
}

const CLOSED_INTENT: Intent | null = null;

/**
 * Owns the Threads state machine. Held by the host (typically the
 * conversation surface or a session-scoped store) — never by a leaf
 * component. The gateway is the only door to the SDK; the VM does not
 * import `MessagingClient` directly.
 *
 * Invariants encoded here:
 * - one active identifier at a time; switching cancels in-flight bootstrap.
 * - snapshot pushes for a non-current identifier are dropped.
 * - `markAsRead` is debounced (250ms) and idempotent failures are non-fatal.
 * - sensitive fields (`previewText`, `body`, names) are never logged —
 *   only event verbs + opaque identifier prefixes go through `log.*`.
 */
export function useThreadViewModel(args: UseThreadViewModelArgs): ThreadViewModel {
  const { gateway } = args;
  const markAsReadDebounceMs = args.markAsReadDebounceMs ?? DEFAULT_MARK_AS_READ_DEBOUNCE_MS;

  const [state, setState] = useState<ThreadState>({ kind: 'closed' });
  const [intent, setIntent] = useState<Intent | null>(CLOSED_INTENT);

  const nonceRef = useRef(0);
  const markAsReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The bootstrap effect: load + subscribe whenever `intent` changes to a
  // non-null value. Cleanup unsubscribes and flips an `aborted` flag so
  // stale resolutions don't write to state.
  useEffect(() => {
    if (intent === null) {
      return;
    }
    let aborted = false;
    let unsubscribe: (() => void) | null = null;

    const apply = (snapshot: ThreadOpenSnapshot): void => {
      if (aborted) {
        return;
      }
      setState((prev) => {
        const sending =
          prev.kind === 'open' && prev.identifier === intent.identifier ? prev.sending : false;
        // Bail out (return the SAME state object → React skips the re-render)
        // when the incoming snapshot is equivalent to what we're already
        // showing. The gateway re-runs `buildSnapshot` on EVERY message in the
        // conversation — a main-chat send or a reply to a DIFFERENT thread — and
        // hands us a fresh-but-equal snapshot (new `ThreadReply` object
        // references). Without this guard that churned the FlatList (a visible
        // "refresh") and re-pinned the panel to the bottom on unrelated
        // activity. We keep `prev` (and its old reply references) so nothing
        // downstream re-renders unless THIS thread's content actually changed.
        if (
          prev.kind === 'open' &&
          prev.identifier === intent.identifier &&
          prev.sending === sending &&
          threadSnapshotUnchanged(prev, snapshot)
        ) {
          return prev;
        }
        return {
          kind: 'open',
          identifier: intent.identifier,
          parent: snapshot.parent,
          replies: snapshot.replies,
          participants: snapshot.participants,
          sending,
        };
      });
    };

    setState({ kind: 'loading', identifier: intent.identifier });

    const bootstrap = async (): Promise<void> => {
      try {
        log.debug('threads.bootstrap', {
          id: identifierPrefix(intent.identifier),
          nonce: intent.nonce,
        });
        unsubscribe = gateway.subscribe(intent.identifier, apply);
        const snapshot = await gateway.load(intent.identifier);
        if (aborted) {
          return;
        }
        apply(snapshot);
      } catch (error) {
        if (aborted) {
          return;
        }
        log.error('threads.bootstrap.failed', error as Error, {
          id: identifierPrefix(intent.identifier),
        });
        setState({
          kind: 'error',
          identifier: intent.identifier,
          error: error as Error,
        });
      }
    };

    void bootstrap();

    return () => {
      aborted = true;
      if (unsubscribe !== null) {
        unsubscribe();
      }
    };
  }, [intent, gateway]);

  // Clear any pending markAsRead timer when the VM unmounts or the
  // identifier changes; rapid focus toggles must not leak timers.
  useEffect(
    () => () => {
      if (markAsReadTimerRef.current !== null) {
        clearTimeout(markAsReadTimerRef.current);
        markAsReadTimerRef.current = null;
      }
    },
    [],
  );

  const open = useCallback((identifier: ThreadIdentifier) => {
    nonceRef.current += 1;
    setIntent({ kind: 'open', identifier, nonce: nonceRef.current });
  }, []);

  const close = useCallback(() => {
    if (markAsReadTimerRef.current !== null) {
      clearTimeout(markAsReadTimerRef.current);
      markAsReadTimerRef.current = null;
    }
    setIntent(CLOSED_INTENT);
    setState({ kind: 'closed' });
  }, []);

  // Captures the most-recent state so `retry` can read the
  // last-known-error identifier without re-rendering on every state
  // change. The VM's `retry` is a no-op outside the error state.
  const stateRef = useRef<ThreadState>(state);
  stateRef.current = state;

  const retry = useCallback(() => {
    const prev = stateRef.current;
    if (prev.kind !== 'error') {
      return;
    }
    nonceRef.current += 1;
    setIntent({ kind: 'retry', identifier: prev.identifier, nonce: nonceRef.current });
  }, []);

  const sendReply = useCallback(
    async (body: string): Promise<void> => {
      // Capture the active identifier synchronously so we don't race against
      // a concurrent close()/open() switching mid-send.
      const currentIdentifier = intent?.identifier ?? null;
      if (currentIdentifier === null) {
        return;
      }
      const trimmed = body.trim();
      if (trimmed.length === 0) {
        return;
      }

      setState((prev) => {
        if (prev.kind !== 'open' || prev.identifier !== currentIdentifier) {
          return prev;
        }
        return { ...prev, sending: true };
      });

      try {
        log.debug('threads.send', { id: identifierPrefix(currentIdentifier) });
        await gateway.sendReply(currentIdentifier, trimmed);
      } catch (error) {
        log.error('threads.send.failed', error as Error, {
          id: identifierPrefix(currentIdentifier),
        });
        throw error;
      } finally {
        setState((prev) => {
          if (prev.kind !== 'open' || prev.identifier !== currentIdentifier) {
            return prev;
          }
          return { ...prev, sending: false };
        });
      }
    },
    [gateway, intent],
  );

  const markAsRead = useCallback(() => {
    if (intent === null) {
      return;
    }
    const currentIdentifier = intent.identifier;
    if (markAsReadTimerRef.current !== null) {
      clearTimeout(markAsReadTimerRef.current);
    }
    markAsReadTimerRef.current = setTimeout(() => {
      markAsReadTimerRef.current = null;
      gateway.markAsRead(currentIdentifier).catch((error: unknown) => {
        log.error('threads.markRead.failed', error as Error, {
          id: identifierPrefix(currentIdentifier),
        });
      });
    }, markAsReadDebounceMs);
  }, [intent, gateway, markAsReadDebounceMs]);

  // Memo the return object so its reference is stable BETWEEN state
  // changes. Without this every call site doing
  // `useEffect(..., [threadVm])` re-runs on every render — and if
  // the effect body calls `threadVm.open(...)` it triggers
  // `setIntent` which re-renders, which makes a new threadVm
  // object, which re-fires the effect → "Maximum update depth
  // exceeded". Mirrors the fix in `ThreadPanelOverlay` that switched
  // to destructured callback deps; this is the defensive companion
  // so any future consumer that depends on the whole vm doesn't
  // hit the same loop.
  return useMemo(
    () => ({ state, open, close, retry, sendReply, markAsRead }),
    [state, open, close, retry, sendReply, markAsRead],
  );
}

/**
 * Opaque 6-char prefix safe for logging (`safe-logging` skill — full
 * identifiers may correlate to user activity timelines).
 */
function identifierPrefix(identifier: ThreadIdentifier): string {
  return identifier.length <= 6 ? identifier : identifier.slice(0, 6);
}

type OpenThreadState = Extract<ThreadState, { kind: 'open' }>;

/**
 * True when a freshly-built snapshot is equivalent — for render purposes — to
 * the currently-shown open state, letting `apply` skip a needless re-render
 * (and the bottom re-pin) when the gateway re-fires for unrelated conversation
 * activity. Compares the parent preview, the reply list (id / status / body /
 * edits / read receipts / author), and participants. Cheap scalar compares;
 * gateway snapshots are infrequent. Reactions are projected by the host (not
 * carried on the snapshot), so they are intentionally out of scope.
 */
function threadSnapshotUnchanged(prev: OpenThreadState, next: ThreadOpenSnapshot): boolean {
  if (prev.replies.length !== next.replies.length) return false;
  if (prev.participants.length !== next.participants.length) return false;

  const a = prev.parent;
  const b = next.parent;
  if (
    a.messageId !== b.messageId ||
    a.authorId !== b.authorId ||
    a.authorName !== b.authorName ||
    a.previewText !== b.previewText ||
    a.timestamp !== b.timestamp ||
    a.deleted !== b.deleted
  ) {
    return false;
  }

  for (let i = 0; i < prev.replies.length; i += 1) {
    const r1 = prev.replies[i]!;
    const r2 = next.replies[i]!;
    if (
      r1.id !== r2.id ||
      r1.status !== r2.status ||
      r1.body !== r2.body ||
      r1.rawBody !== r2.rawBody ||
      r1.editedAt !== r2.editedAt ||
      r1.isSelf !== r2.isSelf ||
      r1.timestamp !== r2.timestamp ||
      r1.authorName !== r2.authorName ||
      r1.authorImageUri !== r2.authorImageUri
    ) {
      return false;
    }
    // Read receipts advance monotonically, so equal length + equal last entry
    // is a sufficient (and cheap) equivalence check.
    const rb1 = r1.readBy ?? [];
    const rb2 = r2.readBy ?? [];
    if (rb1.length !== rb2.length) return false;
    if (rb1.length > 0) {
      const l1 = rb1[rb1.length - 1]!;
      const l2 = rb2[rb2.length - 1]!;
      if (l1.userId !== l2.userId || l1.timestamp !== l2.timestamp) return false;
    }
  }

  for (let i = 0; i < prev.participants.length; i += 1) {
    if (prev.participants[i]!.userId !== next.participants[i]!.userId) return false;
  }

  return true;
}
